import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import { Check, Plus, SignOut } from '@phosphor-icons/react';

const KioskEntry = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [show, setShow] = useState(null);
  const [riderName, setRiderName] = useState('');
  const [horseName, setHorseName] = useState('');
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedRollovers, setSelectedRollovers] = useState({});
  // v264 — Use the backend /rollover-map endpoint so kiosk respects the
  // same same-day rollover rule as rider self-entry (v263). The old
  // client-side inversion of rollover_from_class_ids also had inverted
  // logic that showed unrelated classes as rollover targets.
  const [rolloverMap, setRolloverMap] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    const s = sessionStorage.getItem('kiosk_session');
    if (!s) { navigate('/kiosk'); return; }
    const parsed = JSON.parse(s);
    setSession(parsed);
    setShow(parsed.show);
  }, [navigate]);

  // v264 — Load the same-day-filtered rollover map as soon as the show is
  // known. Falls back to an empty map on error so the kiosk keeps working.
  useEffect(() => {
    if (!show?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/shows/${show.id}/rollover-map`);
        setRolloverMap(data?.rollover_map || {});
      } catch { setRolloverMap({}); }
    })();
  }, [show?.id]);

  const toggleClass = (classId) => {
    setSelectedClasses(prev => prev.includes(classId) ? prev.filter(id => id !== classId) : [...prev, classId]);
  };

  const toggleRollover = (parentId, roId) => {
    setSelectedRollovers(prev => {
      const current = prev[parentId] || [];
      return { ...prev, [parentId]: current.includes(roId) ? current.filter(id => id !== roId) : [...current, roId] };
    });
  };

  const calcFees = () => {
    if (!show) return { classes: 0, rollovers: 0, total: 0 };
    const cf = selectedClasses.reduce((sum, cid) => {
      const cls = show.classes.find(c => c.id === cid);
      return sum + (cls ? parseFloat(cls.entry_fee || 0) : 0);
    }, 0);
    const rf = Object.entries(selectedRollovers).reduce((sum, [, roIds]) => {
      return sum + roIds.reduce((s, roId) => {
        const roCls = show.classes.find(c => c.id === roId) || (show.sidepots || []).find(sp => sp.id === roId);
        return s + (roCls ? parseFloat(roCls.entry_fee || 0) : 0);
      }, 0);
    }, 0);
    const pf = parseFloat(show.processing_fee || 0);
    return { classes: cf, rollovers: rf, processing: pf, total: cf + rf + pf };
  };

  const handleSubmit = async () => {
    if (!riderName.trim()) { toast.error('Rider name is required'); return; }
    if (selectedClasses.length === 0) { toast.error('Select at least one class'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/kiosk/entry', {
        kiosk_id: session.kiosk_id, show_id: session.show_id,
        rider_name: riderName.trim(), horse_name: horseName.trim(),
        class_ids: selectedClasses, rollover_selections: selectedRollovers
      });
      setLastResult(data);
      setSubmitted(true);
      toast.success(`${data.entries_created} entries created — $${data.total_fees.toFixed(2)} due at office`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setRiderName('');
    setHorseName('');
    setSelectedClasses([]);
    setSelectedRollovers({});
    setSubmitted(false);
    setLastResult(null);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('kiosk_session');
    navigate('/kiosk');
  };

  if (!session || !show) return null;

  const fees = calcFees();

  if (submitted && lastResult) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-[#34C759]/10 border border-[#34C759]/30 rounded-lg p-8 mb-6">
            <Check size={48} weight="bold" className="text-[#34C759] mx-auto mb-4" />
            <h2 className="text-2xl font-black text-white mb-2" style={{fontFamily: 'Outfit'}}>Entry Submitted!</h2>
            <p className="text-[#9E9E9E] text-sm mb-4">{lastResult.entries_created} class{lastResult.entries_created !== 1 ? 'es' : ''} entered</p>
            <div className="bg-[#0A0A0A] rounded-lg p-4 mb-4">
              <p className="text-white text-lg font-bold">{riderName}{horseName ? ` on ${horseName}` : ''}</p>
              <p className="text-[#FF9500] text-2xl font-black mt-2">${lastResult.total_fees.toFixed(2)} due at office</p>
              <p className="text-[#FF3B30] text-xs mt-1 font-semibold">PAYMENT DUE — Bring this to the office window</p>
            </div>
            {lastResult.entries.map((e, i) => (
              <div key={i} className="flex justify-between text-sm text-[#9E9E9E] px-2">
                <span>{e.class_name}</span>
                <span>${e.entry_fee.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <button onClick={resetForm} className="w-full py-3 bg-[#0096FF] text-white font-bold rounded-md text-lg mb-3" data-testid="kiosk-next-rider">
            Enter Next Rider
          </button>
          <button onClick={handleLogout} className="w-full py-2 text-[#9E9E9E] text-sm hover:text-white">
            Exit Kiosk
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Kiosk Header */}
      <div className="bg-[#161616] border-b border-[#262626] px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div>
          <p className="text-white font-bold text-sm" style={{fontFamily: 'Outfit'}}>{show.name}</p>
          <p className="text-[#FF9500] text-xs font-semibold">Kiosk Entry — No Login Required</p>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-1 text-[#9E9E9E] hover:text-white text-sm" data-testid="kiosk-logout">
          <SignOut size={16} /> Exit
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Rider Info */}
        <div className="bg-[#161616] border border-[#262626] rounded-lg p-5 mb-6" data-testid="kiosk-rider-info">
          <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Outfit'}}>Rider Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#9E9E9E] mb-1">Rider Name *</label>
              <input type="text" value={riderName} onChange={(e) => setRiderName(e.target.value)}
                placeholder="Full name" autoFocus
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF]"
                data-testid="kiosk-rider-name" />
            </div>
            <div>
              <label className="block text-xs text-[#9E9E9E] mb-1">Horse Name</label>
              <input type="text" value={horseName} onChange={(e) => setHorseName(e.target.value)}
                placeholder="Horse name"
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] rounded-md text-white focus:outline-none focus:border-[#0096FF]"
                data-testid="kiosk-horse-name" />
            </div>
          </div>
        </div>

        {/* Class Selection */}
        <div className="bg-[#161616] border border-[#262626] rounded-lg p-5 mb-6" data-testid="kiosk-class-selection">
          <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Outfit'}}>Select Classes</h3>
          <div className="space-y-2">
            {show.classes.map(cls => {
              const isSelected = selectedClasses.includes(cls.id);
              return (
                <div key={cls.id}>
                  <div onClick={() => toggleClass(cls.id)}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                      isSelected ? 'bg-[#0096FF]/10 border border-[#0096FF]' : 'bg-[#0A0A0A] border border-[#262626] hover:border-[#0096FF]/50'
                    }`} data-testid={`kiosk-class-${cls.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-[#0096FF] border-[#0096FF]' : 'border-[#555]'}`}>
                        {isSelected && <Check size={12} weight="bold" className="text-white" />}
                      </div>
                      <div>
                        <p className="text-white text-sm font-semibold">{cls.name}</p>
                        {cls.scheduled_day && <p className="text-[#9E9E9E] text-xs">{cls.scheduled_day}</p>}
                      </div>
                    </div>
                    <span className="text-[#0096FF] font-bold text-sm">${cls.entry_fee}</span>
                  </div>

                  {/* Rollover targets */}
                  {isSelected && rolloverMap[cls.id] && rolloverMap[cls.id].length > 0 && (
                    <div className="ml-8 mt-1 mb-2 space-y-1">
                      <p className="text-[#FF9500] text-xs font-semibold">Rollover to:</p>
                      {rolloverMap[cls.id].map(roCls => {
                        const roSelected = (selectedRollovers[cls.id] || []).includes(roCls.id);
                        return (
                          <div key={roCls.id} onClick={() => toggleRollover(cls.id, roCls.id)}
                            className={`flex items-center justify-between px-3 py-2 rounded cursor-pointer text-xs ${
                              roSelected ? 'bg-[#FF9500]/10 border border-[#FF9500]/30' : 'bg-[#0A0A0A] hover:bg-[#161616]'
                            }`}>
                            <div className="flex items-center gap-2">
                              <div className={`w-3.5 h-3.5 rounded border ${roSelected ? 'bg-[#FF9500] border-[#FF9500]' : 'border-[#555]'}`} />
                              <span className="text-white">{roCls.name}</span>
                            </div>
                            <span className="text-[#9E9E9E]">${roCls.entry_fee}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Fee Summary + Submit */}
        <div className="bg-[#161616] border border-[#262626] rounded-lg p-5 sticky bottom-4" data-testid="kiosk-summary">
          <div className="space-y-1.5 text-sm mb-4">
            <div className="flex justify-between">
              <span className="text-[#9E9E9E]">Classes ({selectedClasses.length})</span>
              <span className="text-white">${fees.classes.toFixed(2)}</span>
            </div>
            {fees.rollovers > 0 && (
              <div className="flex justify-between">
                <span className="text-[#9E9E9E]">Rollovers</span>
                <span className="text-white">${fees.rollovers.toFixed(2)}</span>
              </div>
            )}
            {fees.processing > 0 && (
              <div className="flex justify-between">
                <span className="text-[#9E9E9E]">Office/Rider Fee</span>
                <span className="text-white">${fees.processing.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-[#262626]">
              <span className="text-white font-bold">Total Due at Office</span>
              <span className="text-[#FF9500] font-bold text-lg">${fees.total.toFixed(2)}</span>
            </div>
          </div>
          <button onClick={handleSubmit} disabled={submitting || selectedClasses.length === 0 || !riderName.trim()}
            className="w-full py-3 bg-[#34C759] text-white font-bold rounded-md text-lg hover:bg-[#2FB350] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="kiosk-submit-entry">
            {submitting ? 'Submitting...' : `Submit Entry — $${fees.total.toFixed(2)} due at office`}
          </button>
          <p className="text-center text-[#FF9500] text-xs mt-2 font-semibold">Payment collected at the office window</p>
        </div>
      </div>
    </div>
  );
};

export default KioskEntry;
